import { __assign, __extends, __rest } from "tslib";
import React from 'react';
import cx from 'classnames';
import bcx from 'bem-classname';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { IconAlert, Info, ArrowRight, IconCheckCircle, Cancel, Close } from '../icons';
function getTouches(evt) {
    return (evt.touches ||
        evt.originalEvent.touches ||
        []);
}
function getAlertEl(el) {
    var targetEl = el;
    while (targetEl.getAttribute('role') !== 'alert') {
        targetEl = targetEl.parentElement;
    }
    return targetEl.children[0];
}
function restoreInitialPosition(el) {
    var container = getAlertEl(el);
    container.style.transform = 'translateX(0)';
}
function isHorizontallyShifted(diffPositionX) {
    return Math.abs(diffPositionX) > 0;
}
function shiftHorizontally(evt, diffPositionX) {
    var alertEl = getAlertEl(evt.target);
    if (diffPositionX > 0) {
        alertEl.style.transform = "translateX(-".concat(diffPositionX, "px)");
    }
    else {
        alertEl.style.transform = "translateX(".concat(Math.abs(diffPositionX), "px)");
    }
}
var TIME_ANIMATION_FADE = 300;
var Alert = (function (_super) {
    __extends(Alert, _super);
    function Alert(props) {
        var _this = _super.call(this, props) || this;
        _this.handlerMouseDown = function (evt) {
            _this.currentPositionX = evt.clientX;
        };
        _this.handlerMouseMove = function (evt) {
            if (!_this.currentPositionX) {
                return;
            }
            var newPositionX = evt.clientX || evt.touches[0].clientX;
            _this.diffPositionX = _this.currentPositionX - newPositionX;
            if (isHorizontallyShifted(_this.diffPositionX)) {
                shiftHorizontally(evt, _this.diffPositionX);
            }
        };
        _this.handlerTouchMove = function (evt) {
            _this.handlerMouseMove(evt);
        };
        _this.handlerMouseUp = function (evt) {
            _this.handlerTouchEnd(evt);
        };
        _this.handlerTouchEnd = function (evt) {
            if (!_this.diffPositionX) {
                _this.diffPositionX = 0;
                _this.currentPositionX = 0;
                return;
            }
            var _a = _this.props, btnAction = _a.btnAction, onClickContainer = _a.onClickContainer, closable = _a.closable;
            var shouldRestoreInitialPosition = !closable
                ? true
                : Math.abs(_this.diffPositionX) < _this.tolaranceDistancePxToCloseAlert;
            if (shouldRestoreInitialPosition) {
                restoreInitialPosition(evt.target);
            }
            else {
                _this.executeActionAfterClose(btnAction || onClickContainer);
            }
            _this.diffPositionX = 0;
            _this.currentPositionX = 0;
        };
        _this.handlerTouchStart = function (evt) {
            if (!_this.currentPositionX && (evt.touches || evt.originalEvent.touches)) {
                var firstTouch = getTouches(evt)[0];
                _this.currentPositionX = (firstTouch === null || firstTouch === void 0 ? void 0 : firstTouch.clientX) || 0;
            }
        };
        _this.state = {
            isClosing: false,
        };
        _this.currentPositionX = 0;
        _this.tolaranceDistancePxToCloseAlert = 100;
        return _this;
    }
    Alert.prototype.componentWillUnmount = function () {
        if (this.timeoutClose)
            clearTimeout(this.timeoutClose);
    };
    Alert.prototype.executeActionAfterClose = function (action) {
        var _this = this;
        this.setState({
            isClosing: true,
        }, function () {
            _this.timeoutClose = setTimeout(function () {
                if (action)
                    action();
            }, TIME_ANIMATION_FADE);
        });
    };
    Alert.prototype.render = function () {
        var _a;
        var _this = this;
        var _b;
        var componentClass = 'alert';
        var _c = this.props, className = _c.className, variant = _c.variant, title = _c.title, btnLabel = _c.btnLabel, altLabel = _c.altLabel, btnAction = _c.btnAction, altAction = _c.altAction, onClickContainer = _c.onClickContainer, btnDisabled = _c.btnDisabled, altDisabled = _c.altDisabled, children = _c.children, message = _c.message, icon = _c.icon, draggable = _c.draggable, closable = _c.closable, errorCode = _c.errorCode, errorLabel = _c.errorLabel, hideIcon = _c.hideIcon, animateOutline = _c.animateOutline, otherProps = __rest(_c, ["className", "variant", "title", "btnLabel", "altLabel", "btnAction", "altAction", "onClickContainer", "btnDisabled", "altDisabled", "children", "message", "icon", "draggable", "closable", "errorCode", "errorLabel", "hideIcon", "animateOutline"]);
        var isClosing = this.state.isClosing;
        var classes = function (value) { return bcx(componentClass, value); };
        var handlersDragsProps = {};
        if (draggable) {
            handlersDragsProps = {
                onTouchStart: this.handlerTouchStart,
                onTouchMove: this.handlerTouchMove,
                onTouchEnd: this.handlerTouchEnd,
                onMouseDown: this.handlerMouseDown,
                onMouseMove: this.handlerMouseMove,
                onMouseUp: this.handlerMouseUp,
            };
        }
        var AlertModel = {
            informative: { icon: React.createElement(Info, { size: "24" }) },
            error: { icon: React.createElement(Cancel, { size: "24" }) },
            neutral: { icon: React.createElement(Info, { size: "24" }) },
            warning: { icon: React.createElement(IconAlert, { size: "24" }) },
            success: { icon: React.createElement(IconCheckCircle, { size: "24" }) },
        };
        var handlerClick = function (handler) { return function (evt) {
            if (!handler)
                return;
            if (closable) {
                _this.executeActionAfterClose(function () { return handler(evt); });
            }
            else {
                handler(evt);
            }
        }; };
        return (React.createElement("div", { role: "alert" },
            React.createElement("div", __assign({ role: "presentation" }, handlersDragsProps, { onKeyDown: function () { }, onClick: handlerClick(onClickContainer), className: cx(className, classes([variant]), 'pt-4', 'collapse-leave', (_a = {
                        'collapse-leave-active': isClosing
                    },
                    _a[classes(['animate-outline'])] = animateOutline,
                    _a)) }),
                React.createElement("div", { className: classes('header') },
                    !hideIcon && (React.createElement("span", { className: cx(classes('icon'), 'flex-shrink') }, icon || ((_b = AlertModel[variant || 'neutral']) === null || _b === void 0 ? void 0 : _b.icon) || React.createElement(Info, null))),
                    title && (React.createElement("div", { className: cx(classes('title'), 'hyphens--manual', 'd-none d-sm-block', 'm-0') }, title)),
                    closable && React.createElement(IconButton, { className: classes('close'), size: "xs", icon: React.createElement(Close, null) })),
                React.createElement("div", { className: cx(bcx(classes('body'), { 'with-icon': !hideIcon })) },
                    title && (React.createElement("div", { className: cx(classes('title'), 'hyphens--manual', 'd-block d-sm-none m-0') }, title)),
                    message && React.createElement("span", { className: classes('message') }, message),
                    errorCode && (React.createElement("div", null,
                        React.createElement("small", null, "".concat(errorLabel, " ").concat(errorCode)))),
                    React.createElement("span", { className: classes('message') }, children),
                    (btnLabel || altLabel) && (React.createElement("p", { className: classes('action') },
                        altLabel && (React.createElement(Button, __assign({ onClick: handlerClick(altAction), variant: "link", className: cx(classes([variant]), 'mb-0', 'pl-0', 'pr-4', 'py-0'), disabled: altDisabled }, otherProps), altLabel)),
                        btnLabel && (React.createElement(Button, __assign({ onClick: handlerClick(btnAction), variant: "link-icon-after", icon: React.createElement(ArrowRight, null), className: cx(classes([variant]), 'mb-0', 'px-0', 'py-0'), disabled: btnDisabled }, otherProps), btnLabel))))))));
    };
    Alert.defaultProps = {
        variant: 'neutral',
        draggable: false,
        closable: false,
        btnLabel: null,
        btnAction: null,
        onClickContainer: null,
        btnDisabled: false,
        altLabel: null,
        altAction: null,
        altDisabled: false,
        message: null,
        btn: null,
        icon: null,
        errorCode: null,
        errorLabel: 'Error',
        hideIcon: false,
        animateOutline: false,
        className: '',
    };
    return Alert;
}(React.Component));
export { Alert };
//# sourceMappingURL=Alert.js.map